import React, { useState } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    TextField,
    FormHelperText,
    Button,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useTranslate, useInput, useNotify } from 'react-admin';
import { Translate, ContentCopy, SwapHoriz } from '@mui/icons-material';
import * as OpenCC from 'opencc-js';

interface PromotionMultiLanguageFieldsProps {
    fields: {
        name: string;
        label: string;
        multiline?: boolean;
        rows?: number;
        required?: boolean;
        helperText?: string;
    }[];
    languages?: { code: string; name: string }[];
    formData?: any;
}

export const PromotionMultiLanguageFields: React.FC<PromotionMultiLanguageFieldsProps> = ({
    fields,
    languages = [
        { code: 'en', name: 'English' },
        { code: 'zh-TW', name: '繁體中文' },
        { code: 'zh-CN', name: '简体中文' }
    ],
    formData
}) => {
    const [currentLang, setCurrentLang] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    const translationsInput = useInput({ source: 'translations' });
    const notify = useNotify();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentLang(newValue);
    };

    // Get translations from form data
    const translations = React.useMemo(() =>
        translationsInput.field.value || [],
        [translationsInput.field.value]
    );
    const currentTranslation = React.useMemo(() =>
        translations[currentLang] || {},
        [translations, currentLang]
    );

    // Initialize translations array if empty
    React.useEffect(() => {
        if (translations.length === 0) {
            const initialTranslations = languages.map(lang => ({
                language: lang.code,
                ...fields.reduce((acc, field) => {
                    return { ...acc, [field.name]: '' };
                }, {})
            }));
            translationsInput.field.onChange(initialTranslations);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const handleFieldChange = (fieldName: string, value: string) => {
        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }
        updatedTranslations[currentLang][fieldName] = value;
        translationsInput.field.onChange(updatedTranslations);
    };

    const handleCopyFromBasic = () => {
        if (!formData) return;

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Copy values from basic information for each field
        fields.forEach(field => {
            if (formData[field.name] !== undefined) {
                const value = formData[field.name] || '';
                updatedTranslations[currentLang][field.name] = value;
            }
        });

        translationsInput.field.onChange(updatedTranslations);
    };

    const handleTranslateFromSimplified = () => {
        // Find the simplified Chinese translation (zh-CN)
        const simplifiedIndex = languages.findIndex(lang => lang.code === 'zh-CN');
        const simplifiedTranslation = translations[simplifiedIndex];

        if (!simplifiedTranslation) {
            notify('Please fill in Simplified Chinese first', { type: 'warning' });
            return;
        }

        // Create converter from Simplified to Traditional
        const converter = OpenCC.Converter({ from: 'cn', to: 'tw' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from simplified to traditional
        fields.forEach(field => {
            const simplifiedValue = simplifiedTranslation[field.name];
            if (simplifiedValue && typeof simplifiedValue === 'string') {
                updatedTranslations[currentLang][field.name] = converter(simplifiedValue);
            }
        });

        translationsInput.field.onChange(updatedTranslations);
        notify('Translated from Simplified Chinese', { type: 'success' });
    };

    const handleTranslateFromTraditional = () => {
        // Find the traditional Chinese translation (zh-TW)
        const traditionalIndex = languages.findIndex(lang => lang.code === 'zh-TW');
        const traditionalTranslation = translations[traditionalIndex];

        if (!traditionalTranslation) {
            notify('Please fill in Traditional Chinese first', { type: 'warning' });
            return;
        }

        // Create converter from Traditional to Simplified
        const converter = OpenCC.Converter({ from: 'tw', to: 'cn' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from traditional to simplified
        fields.forEach(field => {
            const traditionalValue = traditionalTranslation[field.name];
            if (traditionalValue && typeof traditionalValue === 'string') {
                updatedTranslations[currentLang][field.name] = converter(traditionalValue);
            }
        });

        translationsInput.field.onChange(updatedTranslations);
        notify('Translated from Traditional Chinese', { type: 'success' });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Translate sx={{ mr: 1, color: '#00C853' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {translate('resources.promotions.multiLanguage.title')}
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.promotions.multiLanguage.description')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 2 }}>
                <Tabs
                    value={currentLang}
                    onChange={handleTabChange}
                    variant={isMobile ? "fullWidth" : "standard"}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        minHeight: { xs: 40, sm: 48 },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: { xs: 0, sm: 90 },
                            minHeight: { xs: 40, sm: 48 },
                            px: { xs: 1, sm: 2 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }
                    }}
                >
                    {languages.map((lang) => (
                        <Tab key={lang.code} label={lang.name} />
                    ))}
                </Tabs>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyFromBasic}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#00C853',
                            color: '#00C853',
                            '&:hover': {
                                borderColor: '#00A843',
                                backgroundColor: 'rgba(0, 200, 83, 0.08)',
                            }
                        }}
                    >
                        {translate('resources.promotions.multiLanguage.copyFromBasic')}
                    </Button>

                    {/* Show translation button for Traditional Chinese tab */}
                    {languages[currentLang].code === 'zh-TW' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromSimplified}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            從簡體中文翻譯
                        </Button>
                    )}

                    {/* Show translation button for Simplified Chinese tab */}
                    {languages[currentLang].code === 'zh-CN' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromTraditional}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            从繁体中文翻译
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {fields.map((field) => (
                    <Box key={field.name}>
                        <TextField
                            fullWidth
                            label={field.label}
                            value={currentTranslation[field.name] || ''}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                            multiline={field.multiline}
                            rows={field.rows}
                            required={field.required && languages[currentLang].code === 'en'}
                            variant="outlined"
                            placeholder={formData?.[field.name] || ''}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#FAFAFA',
                                    '&:hover': {
                                        backgroundColor: '#F5F5F5',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#FFFFFF',
                                    },
                                },
                            }}
                        />
                        {field.helperText && (
                            <FormHelperText>{field.helperText}</FormHelperText>
                        )}
                    </Box>
                ))}
            </Box>

            {languages[currentLang].code !== 'en' && (
                <Box sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#E8F5E9',
                    borderRadius: 1,
                    border: '1px solid #C8E6C9'
                }}>
                    <Typography variant="caption" color="text.secondary">
                        {translate('resources.promotions.multiLanguage.note')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
